import { useEffect, useMemo, useRef, useState } from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import { ClipPhoto, EASE_OUT, MaskWords, Photo, useMediaQuery } from "../../../kit.jsx";
import { CROWD } from "./album.js";

// One figure stands for ten people, rounded up: 363 -> 37, 1,500 -> 150, 150 -> 15.
export const GROUPS = [
  {
    key: "aaroh",
    name: "AAROH",
    people: 363,
    figures: 37,
    label: "363 young innovators",
    tag: "GRID organised",
    text: "Two days at IIIT Delhi. GRID organised it with Tachyon India and CiPD, IIIT Delhi.",
  },
  {
    key: "aurora",
    name: "Aurora",
    people: 1500,
    figures: 150,
    label: "1,500 participants",
    tag: "GRID's own hackathon",
    text: "GRID's own global hackathon, with participants from 27+ countries.",
  },
  {
    key: "meta",
    name: "Metamorphosis",
    people: 150,
    figures: 15,
    label: "150 students",
    tag: "GRID as outreach partner",
    text: "Drawn in outline. Tale of Humankind Foundation and IIT Delhi organised it, and GRID was the outreach partner.",
  },
];

const TOTAL_FIGURES = GROUPS.reduce((sum, group) => sum + group.figures, 0);
const TOTAL_PEOPLE = GROUPS.reduce((sum, group) => sum + group.people, 0);

const CELL_W = 22;
const CELL_H = 40;
const LABEL_H = 36;
const GAP = 12;

// A person: round head, rounded body. Drawn in a 22 x 40 cell.
export function FigureGlyph() {
  return (
    <>
      <circle cx="11" cy="8" r="5" />
      <path d="M3.5 36 V22 Q3.5 15.5 11 15.5 Q18.5 15.5 18.5 22 V36 Z" />
    </>
  );
}

function layout(cols) {
  const separate = [];
  const labels = [];
  let y = 0;
  GROUPS.forEach((group) => {
    labels.push({ key: group.key, y: y + LABEL_H - 14, text: `${group.name} · ${group.label}` });
    y += LABEL_H;
    for (let k = 0; k < group.figures; k += 1) {
      separate.push({ group: group.key, k, x: (k % cols) * CELL_W, y: y + Math.floor(k / cols) * CELL_H });
    }
    y += Math.ceil(group.figures / cols) * CELL_H + GAP;
  });
  const merged = separate.map((_, i) => ({ x: (i % cols) * CELL_W, y: LABEL_H + Math.floor(i / cols) * CELL_H }));
  return { separate, merged, labels, width: cols * CELL_W, height: y - GAP };
}

const fmt = (value) => Math.round(value).toLocaleString("en-US");

const STEPS = [
  { title: "Start with one full hall.", text: "That is AAROH on 1–2 August 2026. Keep scrolling and every ten people become one figure, programme by programme.", total: 0 },
  { ...GROUPS[0], title: `${GROUPS[0].name} · ${GROUPS[0].label}`, total: GROUPS[0].people },
  { ...GROUPS[1], title: `${GROUPS[1].name} · ${GROUPS[1].label}`, total: GROUPS[0].people + GROUPS[1].people },
  { ...GROUPS[2], title: `${GROUPS[2].name} · ${GROUPS[2].label}`, total: TOTAL_PEOPLE },
  {
    title: `${fmt(TOTAL_PEOPLE)} participants, together.`,
    text: "Two days at IIIT Delhi, a global hackathon and a week at IIT Delhi.",
    total: TOTAL_PEOPLE,
  },
];

const GROUP_PHASE = { aaroh: 1, aurora: 2, meta: 3 };

function Legend() {
  return (
    <ul className="ab-iso__legend" aria-label="Key">
      <li><span className="ab-iso__key is-aaroh" aria-hidden="true" />AAROH, GRID organised</li>
      <li><span className="ab-iso__key is-aurora" aria-hidden="true" />Aurora, GRID's own hackathon</li>
      <li><span className="ab-iso__key is-meta" aria-hidden="true" />Metamorphosis, GRID as outreach partner</li>
      <li className="ab-iso__scale">1 figure = 10 people, rounded up</li>
    </ul>
  );
}

// Static, fully labelled grid. Used on phones and with reduced motion.
function StaticIsotype({ cols }) {
  return (
    <section className="ab-iso ab-iso--static" id="people" aria-labelledby="ab-iso-title">
      <header className="ab-iso__head">
        <MaskWords id="ab-iso-title" className="ab-h2" text="Every programme, one figure per ten people." />
        <Legend />
      </header>
      <ClipPhoto className="ab-iso__crowd" src={CROWD.srcSmall ?? CROWD.src} alt={CROWD.alt} caption={`${CROWD.caption} Below, every ten people become one figure.`} sizes="100vw" />
      <div className="ab-iso__blocks">
        {GROUPS.map((group) => (
          <figure key={group.key} className="ab-iso__block">
            <figcaption>
              <strong>{group.name}</strong> {group.label} · {group.tag}
            </figcaption>
            <svg
              viewBox={`0 0 ${cols * CELL_W} ${Math.ceil(group.figures / cols) * CELL_H}`}
              role="img"
              aria-label={`${group.figures} figures for ${group.label} at ${group.name}`}
            >
              {Array.from({ length: group.figures }, (_, k) => (
                <g key={k} className={`ab-fig is-${group.key} is-on`} transform={`translate(${(k % cols) * CELL_W} ${Math.floor(k / cols) * CELL_H})`}>
                  <g className="ab-fig__body"><FigureGlyph /></g>
                </g>
              ))}
            </svg>
          </figure>
        ))}
      </div>
      <p className="ab-iso__sum">
        <strong>{fmt(TOTAL_PEOPLE)} participants</strong> across AAROH, Aurora and Metamorphosis.
      </p>
    </section>
  );
}

function PinnedIsotype() {
  const ref = useRef(null);
  const [phase, setPhase] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const { separate, merged, labels, width, height } = useMemo(() => layout(26), []);
  const count = useMotionValue(0);
  const countText = useTransform(count, (value) => fmt(value));
  const rail = useTransform(scrollYProgress, (value) => Math.min(1, Math.max(0, value / 0.86)));

  useMotionValueEvent(scrollYProgress, "change", (value) => {
    const next = value < 0.1 ? 0 : value < 0.3 ? 1 : value < 0.5 ? 2 : value < 0.7 ? 3 : 4;
    setPhase((previous) => (previous === next ? previous : next));
  });

  useEffect(() => {
    const controls = animate(count, STEPS[phase].total, { duration: 1.1, ease: EASE_OUT });
    return () => controls.stop();
  }, [count, phase]);

  const step = STEPS[phase];
  const regrouped = phase >= 4;

  return (
    <section className="ab-iso ab-iso--pinned" id="people" ref={ref} aria-labelledby="ab-iso-title">
      <div className="ab-iso__sticky">
        <div className="ab-iso__panel">
          <p className="ab-kicker">People, counted</p>
          <h2 id="ab-iso-title" className="ab-iso__title">Every programme, one figure per ten people.</h2>
          <div className="ab-iso__count" aria-live="polite">
            <span className="sr-only">{fmt(step.total)} so far</span>
            <motion.strong aria-hidden="true">{countText}</motion.strong>
            <small>participants so far</small>
          </div>
          <div className="ab-iso__step">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={phase}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10, transition: { duration: 0.16 } }}
                transition={{ duration: 0.5, ease: EASE_OUT }}
              >
                <strong>{step.title}</strong>
                {step.tag && <span className={`ab-iso__tag is-${step.key}`}>{step.tag}</span>}
                <p>{step.text}</p>
              </motion.div>
            </AnimatePresence>
          </div>
          <ol className="ab-iso__chapters" aria-label="Chapters">
            <span className="ab-iso__rail" aria-hidden="true"><motion.span style={{ scaleY: rail }} /></span>
            {["AAROH", "Aurora", "Metamorphosis", "All together"].map((name, index) => (
              <li key={name} className={phase >= index + 1 ? "is-done" : undefined}>{name}</li>
            ))}
          </ol>
        </div>
        <div className="ab-iso__stage">
          <div className="ab-iso__canvas">
          <figure className={`ab-iso__plate ${phase >= 1 ? "is-gone" : ""}`} aria-hidden={phase >= 1 ? "true" : undefined}>
            <Photo src={CROWD.src} alt={CROWD.alt} width={CROWD.width} height={CROWD.height} sizes="60vw" />
            <figcaption>{CROWD.caption}</figcaption>
          </figure>
          <svg
            className={`ab-iso__svg ${regrouped ? "is-merged" : ""}`}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={`${TOTAL_FIGURES} person figures. ${GROUPS.map((g) => `${g.figures} for ${g.name}`).join(", ")}.`}
          >
            {labels.map((label) => (
              <text key={label.key} className={`ab-iso__label ${phase >= GROUP_PHASE[label.key] && !regrouped ? "is-on" : ""}`} x="0" y={label.y}>
                {label.text}
              </text>
            ))}
            <text className={`ab-iso__label ab-iso__label--all ${regrouped ? "is-on" : ""}`} x="0" y={LABEL_H - 14}>
              All three programmes · {fmt(TOTAL_PEOPLE)} participants
            </text>
            {separate.map((figure, i) => {
              const target = regrouped ? merged[i] : figure;
              const on = phase >= GROUP_PHASE[figure.group];
              return (
                <g
                  key={i}
                  className="ab-fig__pos"
                  style={{ transform: `translate(${target.x}px, ${target.y}px)`, transitionDelay: `${(regrouped ? i : TOTAL_FIGURES - i) * 3}ms` }}
                >
                  <g className={`ab-fig is-${figure.group} ${on ? "is-on" : ""}`} style={{ "--dk": `${figure.k * (figure.group === "aurora" ? 5 : 14)}ms` }}>
                    <g className="ab-fig__body"><FigureGlyph /></g>
                  </g>
                </g>
              );
            })}
          </svg>
          </div>
          <Legend />
        </div>
      </div>
    </section>
  );
}

export default function PeopleIsotype() {
  const reduceMotion = useReducedMotion();
  const wide = useMediaQuery("(min-width: 900px) and (min-height: 620px)");
  const narrow = useMediaQuery("(max-width: 599px)");
  if (reduceMotion || !wide) return <StaticIsotype cols={narrow ? 13 : 26} />;
  return <PinnedIsotype />;
}
